import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Sidebar, SidebarContent, SidebarHeader, useSidebar } from 'components/ui/sidebar';
import { X } from 'lucide-react';
import { SidebarCollapse } from 'iconoir-react';
import { Icon, IconName } from '../menu-icon/menu-icon';
import { SidebarHeaderConfig, SidebarHeaderVariant } from '../../../pages/main/main-layout';
import { useChatStore } from '../../../state/store/chat/use-chat-store';
import { ConversationSearch } from '../conversation-search';
import { deleteConversationById } from '../../../services/storage-service';
import { useToast } from '../../../hooks/use-toast';
import { CustomDialog } from '../custom-dialog/custom-dialog';
import { hasValidConversationComponentRoute, hasValidPlanForStartViaShareLink } from 'pages/services/utilityService';
import { useTranslation } from 'react-i18next';
import { DebouncedInput } from '../debounced-input/debounced-input';
import { useConfig } from 'hooks/use-config';
import { useSubscriptionStore } from 'state/store/subscription';
import { AgentsSection } from 'features/chat/components/agents-section';
import { ProjectsSection } from 'features/projects/components/project-section';
import { ProjectActionDialogs } from 'features/projects/components/project-actions';
import { modalOption } from '/models/sidebar';
import { refetchProject } from 'features/projects/helpers/query-utils';
import { NewProjectModal } from 'features/projects/components/new-project-modal';
import { useQueryClient } from '@tanstack/react-query';
import { useUpdateProject } from 'features/projects/hooks/use-create-project';
import { PLANS } from 'constant/plan';
import { cn } from 'lib/utils';
import { DndContext, PointerSensor, useSensor, useSensors, DragOverlay } from '@dnd-kit/core';

import { useTheme } from 'components/core/theme-provider';
import { ConversationContextMenu } from './conversation-context-menu';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { IndividualChatsSection } from 'features/chat/components/individual-chats-section';
import { GroupChatsSection } from 'features/chat/components/group-chats-section';
import { AppSidebarFooter } from './sidebar-footer';
import { SidebarPrimaryNavItem } from './sidebar-primary-nav-item';
import { SidebarConfigureNavItem } from './sidebar-configure-nav-item';
import { SidebarConfigurePanel } from './sidebar-configure-panel';
import { isConfigureRoute, shouldOpenConfigureOverlayOnPathChange } from 'constant/configure-nav';
import { SidebarNavTooltip } from './sidebar-nav-tooltip';
import {
  sidebarNavItemClassName,
  sidebarNavItemIconOnlyClassName,
} from './sidebar-nav-item-styles';
import { SIDEBAR_NAV_ICON_SIZE, SIDEBAR_NAV_ICON_STROKE } from './sidebar-nav-icon';
// import { MyAssistantsSection } from 'features/chat/components/my-assistants-section';
import { useGetAgents } from 'features/chat/hooks/use-agents';
import { isGroupChatEnabled } from 'config/feature-flags';
import {
  markConversationDeletedLocally,
  clearLocallyDeletedConversation,
  removeAgentConversationFromCache,
} from 'features/chat/utils/agent-sidebar-sync';

import groupDark from 'assets/images/selise-ai-logo-dark.svg';
import groupLight from 'assets/images/selise-ai-logo.svg';
import { useWhiteLabelStore } from 'state/store/white-label';
import { useGroupShareModal } from 'hooks/use-group-share-modal';
import { GroupShareModal } from 'features/chat/components/GroupShareModal';
import { useCommonStore } from 'state/store/common/useCommonStore';
import { useAgentSelectionStore } from 'state/store/chat/use-agent-selection-store';
import { sendInvitation } from 'features/group-chat/services/group-services';
import seliseLogo from 'assets/images/selise-ai-logo.svg';
import seliseLogoDark from 'assets/images/selise-ai-logo-dark.svg';
import { isPathActive } from 'utils/is-path-active';


export function LogoSidebarHeader({
  open,
  isMobile,
  setOpenMobile,
}: SidebarHeaderConfig) {
  const { theme } = useTheme();
  const logoSrc = theme === 'dark' ? seliseLogoDark : seliseLogo;
  return (
    <div className="relative h-10 w-full">
      <img
        src={logoSrc}
        alt="logo"
        className={`absolute left-4 top-1 h-8 w-auto max-w-full transition-all duration-300 ${open || isMobile ? 'opacity-100' : 'opacity-0 pointer-events-none'
          }`}
      />

      <img
        src={logoSrc}
        alt="smallLogo"
        className={`absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2 size-8 object-cover object-left transition-all duration-300 ${open || isMobile ? 'opacity-0 pointer-events-none' : 'opacity-100'
          }`}
      />

      {isMobile && (
        <button
          className="absolute right-4 top-1/2 transform -translate-y-1/2"
          onClick={setOpenMobile ? () => setOpenMobile(false) : undefined}
          aria-label="Close sidebar"
        >
          <X size={20} />
        </button>
      )}
    </div>
  );
}

export function IconsSidebarHeader({
  strokeWidth = 2.2,
  toggleSidebar,
  currentPlan,
  // disableNewChat
  open,
  isMobile,
}: SidebarHeaderConfig) {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const { settings: whiteLabelSettings } = useWhiteLabelStore();
  const showExpanded = isMobile || open;
  const showSearch = (currentPlan?.planCode ?? PLANS.UNLIMITED) !== PLANS.NOPLAN;

  if (!showExpanded) {
    return (
      <SidebarNavTooltip label={t('OPEN_SIDEBAR') || 'Open sidebar'}>
        <button
          type="button"
          onClick={toggleSidebar}
          className={cn(
            sidebarNavItemClassName,
            sidebarNavItemIconOnlyClassName,
            theme === 'dark' ? 'hover:bg-primary-200/10' : 'hover:bg-primary-600/10'
          )}
          aria-label={t('OPEN_SIDEBAR') || 'Open sidebar'}
        >
          <SidebarCollapse
            width={SIDEBAR_NAV_ICON_SIZE}
            height={SIDEBAR_NAV_ICON_SIZE}
            strokeWidth={strokeWidth ?? SIDEBAR_NAV_ICON_STROKE}
            className={cn(
              'shrink-0 -translate-x-px',
              theme === 'dark' ? 'text-true-white' : 'text-[hsl(var(--grey-600))]'
            )}
          />
        </button>
      </SidebarNavTooltip>
    );
  }

  // Determine which logo to use based on theme and white-label settings
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const logoSrc = theme === 'dark'
    ? (whiteLabelSettings?.logoUrlDarkMode || groupDark)
    : (whiteLabelSettings?.logoUrl || groupLight);

  return (
    <div className=" w-full flex items-center justify-between px-2">
      <img src={theme === 'dark' ? seliseLogoDark : seliseLogo} alt="logo" className="2xl:h-8 h-7 w-auto" />

      <div className="h-full flex items-center justify-end gap-2">
        {showSearch && <ConversationSearch />}
        <button
          onClick={toggleSidebar}
          className={`rounded-lg ${theme === 'dark' ? 'hover:bg-primary-200/10' : 'hover:bg-primary-600/10'}`}
          aria-label="Toggle sidebar"
        >
          <SidebarCollapse
            width={20}
            height={20}
            strokeWidth={strokeWidth}
            className={theme === 'dark' ? 'text-true-white' : 'text-[hsl(var(--grey-600))]'}
          />
        </button>
      </div>
    </div>
  );
}

export function AppSidebar({ type, logo, smallLogo, strokeWidth }: SidebarHeaderConfig) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { pathname } = useLocation();
  const { conversationId: conversationIdFromParams, chatId: agentChatIdFromParams } =
    useParams<{ conversationId?: string; chatId?: string }>();
  const activeSidebarChatId = conversationIdFromParams ?? agentChatIdFromParams;
  const queryClient = useQueryClient();
  const { mutateAsync: updateProject } = useUpdateProject();
  const { setOpenMobile, open, isSidebarOverlay, isConfigureInline, toggleSidebar, configureOpen, setConfigureOpen, setOpen } = useSidebar();
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [conversationState, setConversationState] = useState<{
    id: string | null;
    type: 'delete' | 'archive' | 'rename' | 'leave' | null;
  }>({
    id: null,
    type: null,
  });
  // Drag and drop states
  const [isDragging, setIsDragging] = useState<boolean>(false);
  const [activeConversation, setActiveConversation] = useState<{
    id: string;
    title: string;
  } | null>(null);

  const [individualPage, setIndividualPage] = useState(0);
  const [groupPage, setGroupPage] = useState(0);
  const pageSize = 50;
  const flatConversationList = useChatStore((state) => state.sidebarConversationsList);
  const totalIndividualConversationsCount = useChatStore((state) => state.totalIndividualConversationsCount);
  const totalGroupConversationsCount = useChatStore((state) => state.totalGroupConversationsCount);

  // Agent chats live nested under their agent in the "Agents" section, not in Recent Chats.
  const individualChats = flatConversationList.filter((c) => !c.IsGroup && !c.AgentId);
  const groupChats = flatConversationList.filter((c) => c.IsGroup && !c.AgentId);

  const hasMoreIndividualChats = individualChats.length < totalIndividualConversationsCount;
  const hasMoreGroupChats = groupChats.length < totalGroupConversationsCount;
  const fetchSidebarConversations = useChatStore((state) => state.fetchSidebarConversations);
  const sidebarProjectsList = useChatStore((state) => state.sidebarProjectsList);
  const fetchAndUpdateProjectAllConversations = useChatStore(
    (state) => state.fetchAndUpdateProjectAllConversations
  );
  const isSidebarLoading = useChatStore((state) => state.isSidebarLoading);
  const isSidebarIndividualLoading = useChatStore((state) => state.isSidebarIndividualLoading);
  const isSidebarGroupLoading = useChatStore((state) => state.isSidebarGroupLoading);
  const { setIsIncognitoChat } = useChatStore();
  const removeConversationFromSidebarById = useChatStore(
    (state) => state.removeConversationFromSidebarById
  );
  const updateConversationTitleInStore = useChatStore(
    (state) => state.updateConversationTitleInStore
  );
  const shareTokens = useChatStore((state) => state.shareTokens);
  const { configurationSettings } = useConfig();

  const { currentPlan } = useSubscriptionStore();

  const { data: agentsData } = useGetAgents({
    limit: 100,
    offset: 0,
    project_key: configurationSettings?.blocksKey || '',
  });

  const [isRenameDialogOpen, setIsRenameDialogOpen] = useState(false);
  const [isRenameLoading, setIsRenameLoading] = useState(false);
  const [isAddProjectModalOpen, setIsAddProjectModalOpen] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [conversationIdToAddToProject, setConversationIdToAddToProject] = useState<
    string | undefined
  >(undefined);
  const theme = useTheme();
  const [selectedConversationIdForShare, setSelectedConversationIdForShare] = useState<
    string | undefined
  >(undefined);
  // Configure DnD sensors
  const dragTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  );

  useEffect(() => {
    return () => {
      if (dragTimeoutRef.current) {
        clearTimeout(dragTimeoutRef.current);
      }
    };
  }, []);

  // Use custom hook for group share modal management
  const {
    showModal,
    setShowModal,
    shareToken,
    setShareToken,
    handleOpenModal,
    isLoading: isLoadingModal,
  } = useGroupShareModal(selectedConversationIdForShare);

  // Treat absent plan as UNLIMITED — gates pass when no subscription service exists.
  const effectivePlanName = currentPlan?.planCode ?? PLANS.UNLIMITED;

  useEffect(() => {
    if (effectivePlanName !== PLANS.NOPLAN && configurationSettings) {
      fetchSidebarConversations(configurationSettings, { page: individualPage, pageSize, isGroupConversation: false });
    }
  }, [fetchSidebarConversations, configurationSettings, individualPage, pageSize, effectivePlanName]);

  useEffect(() => {
    if (effectivePlanName !== PLANS.NOPLAN && configurationSettings) {
      fetchSidebarConversations(configurationSettings, { page: groupPage, pageSize, isGroupConversation: true });
    }
  }, [fetchSidebarConversations, configurationSettings, groupPage, pageSize, effectivePlanName]);

  const sidebarScrollRef = useRef<HTMLDivElement | null>(null);
  const loadMoreLockRef = useRef(false);
  const hasMoreIndividualChatsRef = useRef(hasMoreIndividualChats);
  const hasMoreGroupChatsRef = useRef(hasMoreGroupChats);

  hasMoreIndividualChatsRef.current = hasMoreIndividualChats;
  hasMoreGroupChatsRef.current = hasMoreGroupChats;

  useEffect(() => {
    if (!isSidebarLoading) {
      loadMoreLockRef.current = false;
    }
  }, [isSidebarLoading]);

  const loadMoreIfNearBottom = useCallback(() => {
    const root = sidebarScrollRef.current;
    if (!root || loadMoreLockRef.current) return;
    if (useChatStore.getState().isSidebarLoading) return;
    if (root.scrollHeight <= root.clientHeight + 160) return;
    if (root.scrollHeight - root.scrollTop - root.clientHeight > 160) return;

    if (hasMoreIndividualChatsRef.current) {
      loadMoreLockRef.current = true;
      setIndividualPage((prev) => prev + 1);
      return;
    }

    if (hasMoreGroupChatsRef.current) {
      loadMoreLockRef.current = true;
      setGroupPage((prev) => prev + 1);
    }
  }, []);

  useEffect(() => {
    if (isSidebarLoading) return;
    loadMoreIfNearBottom();
  }, [isSidebarLoading, individualChats.length, groupChats.length, loadMoreIfNearBottom]);

  const createConversationMenuOptions = (
    conversationId: string,
    conversationTitle: string,
    shareTokens: Record<string, string>,
    isGroupChat?: boolean,
  ): modalOption[] => {
    // Get share token from the centralized store (updated in real-time)
    const shareToken = shareTokens[conversationId];
    const hasShareToken = !!shareToken;

    // Get conversation from sidebar list to check CreatedBy
    const conversation = flatConversationList.find((c) => c.ConversationId === conversationId);
    const currentUser = useCommonStore.getState().user;
    const isCreator = conversation?.CreatedBy === currentUser?.id;

    const baseOptions: modalOption[] = [
      {
        id: 'rename',
        name: t('RENAME'),
        icon: 'Pencil' as IconName,
        callback: () => {
          console.log('Rename Conversation:', conversationId);
          setIsRenameDialogOpen(true);
          setNewTitle(conversationTitle || '');
          setConversationState({
            id: conversationId,
            type: 'rename',
          });
        },
      }
    ];

    if (isGroupChatEnabled && hasValidPlanForStartViaShareLink(currentPlan)) {
      baseOptions.push({
        id: 'start-group-chat',
        name: hasShareToken
          ? (t('ADD_PARTICIPANT_VIA_LINK') || 'Add Participant via link')
          : (t('START_GROUP_CHAT') || 'Start group chat'),
        icon: 'UserRoundPlus' as IconName,
        isDisabled: isLoadingModal,
        callback: () => {
          console.log('Start Group Chat:', conversationId);
          setSelectedConversationIdForShare(conversationId);
          handleOpenModal();
        },
      });
    }

    if (!isGroupChat) {
      baseOptions.push({
        id: 'add-to-project',
        type: 'submenu' as const,
        name: t('ADD_TO_PROJECT'),
        icon: 'FolderClosed' as IconName,
        subOptions: [
          {
            id: 'new-project',
            name: t('NEW_PROJECT'),
            icon: 'FolderPlus' as IconName,
            callback: () => {
              setIsAddProjectModalOpen(true);
              setConversationIdToAddToProject(conversationId);
            },
          },
          ...sidebarProjectsList.map((project) => ({
            id: project.id,
            name: project.name,
            icon: 'FolderClosed' as IconName,
            callback: () => handleAddToProject(project.id, conversationId),
          })),
        ],
      });
      baseOptions.push({
        id: 'archive',
        name: t('ARCHIVE'),
        icon: 'Archive' as IconName,
        callback: () => {
          console.log('Archive Conversation:', conversationId);
          setIsConfirmDialogOpen(true);
          setConversationState({
            id: conversationId,
            type: 'archive',
          });
        },
      });
    }

    if (isCreator) {
      baseOptions.push({
        id: 'delete',
        name: t('DELETE'),
        icon: 'Trash2' as IconName,
        callback: () => {
          console.log('Delete Conversation:', conversationId);
          setIsConfirmDialogOpen(true);
          setConversationState({
            id: conversationId,
            type: 'delete',
          });
        },
      });
    } else {
      baseOptions.push({
        id: 'leave',
        name: t('LEAVE') || 'Leave',
        icon: 'LogOut' as IconName,
        callback: () => {
          console.log('Leave Conversation:', conversationId);
          setIsConfirmDialogOpen(true);
          setConversationState({
            id: conversationId,
            type: 'leave',
          });
        },
      });
    }

    return baseOptions;
  };

  const handleAddToProject = async (projectId: string, conversationId: string) => {
    try {
      const toProject = sidebarProjectsList.find((p) => p.id === projectId);
      if (!toProject) return;
      const payload = {
        projectId: projectId,
        conversationId: conversationId,
      };
      await updateProject({ payload: payload, skipInvalidate: true });
      await fetchAndUpdateProjectAllConversations(configurationSettings, projectId);
      removeConversationFromSidebarById(conversationId);
      toast({
        variant: 'success',
        title: t('SUCCESS'),
        description: t('MOVE_SUCCESS_DESCRIPTION'),
      });
    } catch {
      toast({
        variant: 'destructive',
        title: t('MOVE_FAILED'),
        description: t('MOVE_FAILED_DESCRIPTION'),
      });
    } finally {
      setIsConfirmDialogOpen(false);
      setIsLoading(false);
    }
  };

  const handleRename = async (conversationId: string) => {
    try {
      setIsRenameLoading(true);
      await updateProject({ payload: { conversationId, conversationTitle: newTitle } });
      updateConversationTitleInStore(conversationId, newTitle, { isManual: true });
      setIsRenameDialogOpen(false);
      setIsRenameLoading(false);
      toast({
        variant: 'success',
        title: t('SUCCESS'),
        description: t('RENAME_SUCCESS_DESCRIPTION'),
      });
    } catch (error) {
      setIsRenameDialogOpen(false);
      setIsRenameLoading(false);
      toast({
        variant: 'destructive',
        title: t('RENAME_FAILED'),
        description: t('RENAME_FAILED_DESCRIPTION'),
      });
      console.error('Error renaming conversation:', error);
    }
  };

  const handleArchive = async (conversationId: string) => {
    try {
      setIsLoading(true);
      await updateProject({ payload: { isArchived: true, conversationId } });
      removeConversationFromSidebarById(conversationId);
      setIsConfirmDialogOpen(false);
      setIsLoading(false);
      if (conversationIdFromParams && conversationIdFromParams === conversationId)
        navigate('/chat');
      toast({
        variant: 'success',
        title: t('SUCCESS'),
        description: t('ARCHIVE_SUCCESS_DESCRIPTION'),
      });
    } catch (error) {
      setIsConfirmDialogOpen(false);
      setIsLoading(false);
      toast({
        variant: 'destructive',
        title: t('ARCHIVE_FAILED'),
        description: t('ARCHIVE_FAILED_DESCRIPTION'),
      });
      console.error('Error archiving conversation:', error);
    }
  };

  const handleDelete = async (conversationId: string) => {
    try {
      setIsLoading(true);
      markConversationDeletedLocally(conversationId);
      await deleteConversationById(conversationId, configurationSettings);
      removeConversationFromSidebarById(conversationId);
      removeAgentConversationFromCache(queryClient, conversationId);
      setIsConfirmDialogOpen(false);
      setIsLoading(false);
      if (conversationIdFromParams && conversationIdFromParams === conversationId)
        navigate('/chat');
      toast({
        variant: 'success',
        title: t('SUCCESS'),
        description: t('DELETE_SUCCESS_DESCRIPTION'),
      });
    } catch (error) {
      clearLocallyDeletedConversation(conversationId);
      setIsConfirmDialogOpen(false);
      setIsLoading(false);
      toast({
        variant: 'destructive',
        title: t('DELETE_FAILED'),
        description: t('DELETE_FAILED_DESCRIPTION'),
      });
      console.error('Error deleting conversation:', error);
    }
  };

  const handleLeave = async (conversationId: string) => {
    try {
      setIsLoading(true);
      const currentUser = useCommonStore.getState().user;
      if (!currentUser?.id || !configurationSettings) {
        throw new Error('User or configuration not available');
      }

      // Get conversation title for the payload
      const conversation = flatConversationList.find((c) => c.ConversationId === conversationId);
      const conversationTitle = conversation?.ConversationTitle || '';

      const payload = {
        invitationType: 'REMOVE_USER' as const,
        userIds: [currentUser.id],
        conversationId: conversationId,
        conversationTitle: conversationTitle,
      } as any; // Type assertion needed as InvitationPayload interface doesn't include userIds and conversationTitle

      await sendInvitation(payload, configurationSettings);
      removeConversationFromSidebarById(conversationId);
      setIsConfirmDialogOpen(false);
      setIsLoading(false);

      if (conversationIdFromParams && conversationIdFromParams === conversationId) {
        navigate('/chat');
      }

      toast({
        variant: 'success',
        title: t('SUCCESS'),
        description: t('LEAVE_SUCCESS_DESCRIPTION') || 'You have left the conversation',
      });
    } catch (error) {
      setIsConfirmDialogOpen(false);
      setIsLoading(false);
      toast({
        variant: 'destructive',
        title: t('LEAVE_FAILED') || 'Failed to leave',
        description: t('LEAVE_FAILED_DESCRIPTION') || 'Failed to leave the conversation',
      });
      console.error('Error leaving conversation:', error);
    }
  };

  const handleConfirmAction = () => {
    if (conversationState.id && conversationState.type === 'archive') {
      handleArchive(conversationState.id);
    } else if (conversationState.id && conversationState.type === 'delete') {
      handleDelete(conversationState.id);
    } else if (conversationState.id && conversationState.type === 'rename') {
      handleRename(conversationState.id);
    } else if (conversationState.id && conversationState.type === 'leave') {
      handleLeave(conversationState.id);
    }
  };

  // Drag and drop handlers
  const handleDragStart = (event: any) => {
    const conversationId = event.active.id;
    const conversation = flatConversationList.find((c) => c.ConversationId === conversationId);

    setIsDragging(true);
    setActiveConversation({
      id: conversationId,
      title: conversation?.ConversationTitle || t('NEW_CHAT'),
    });
  };

  const handleDragEnd = (event: any) => {
    const { active, over } = event;
    setIsDragging(false);
    if (over && over.id) {
      const conversationId = active.id;
      const projectId = over.id;

      const project = sidebarProjectsList.find((p) => p.id === projectId);

      toast({
        variant: 'info',
        title: t('MOVING_CONVERSATION'),
        description: `Moving "${activeConversation?.title}" to "${project?.name}"`,
        duration: 2000,
      });

      handleAddToProject(projectId, conversationId);
    }

    setTimeout(() => {
      setActiveConversation(null);
    }, 0);
  };

  const handleDragCancel = () => {
    setIsDragging(false);
    setActiveConversation(null);
    toast({
      title: 'Drag cancelled',
      description: 'Conversation was not moved',
      variant: 'destructive',
      duration: 2000,
    });

    setTimeout(() => {
      setIsDragging(false);
      setActiveConversation(null);
    }, 150);
  };

  const handleDragOver = (event: any) => {
    const { over } = event;
    if (over) {
      const project = sidebarProjectsList.find((p) => p.id === over.id);
      console.log(`Dragging over project: ${project?.name}`);
    }
  };

  const setNewChat = useChatStore((state) => state.setNewChat);
  const disableNewChat = useChatStore((state) => state.disableNewChat);
  const clearUserSelection = useAgentSelectionStore((state) => state.clearUserSelection);
  const userId = useCommonStore((state) => state.user?.id);

  const onNewChatClick = () => {
    if (disableNewChat) return;
    if (configureOpen) {
      setConfigureOpen(false);
      if (!isConfigureInline) {
        setOpen(true);
      }
    }
    if (isSidebarOverlay) {
      setOpenMobile(false);
    }
    setNewChat(true);
    setIsIncognitoChat(false);
    if (userId) {
      clearUserSelection(userId);
    }
    navigate({ pathname: '/chat', search: '' });
  };

  const handleConfigureClick = () => {
    setConfigureOpen(true);
    if (isSidebarOverlay) {
      setOpenMobile(true);
    } else {
      setOpen(false);
    }
    if (pathname !== '/configure') {
      navigate('/configure');
    }
  };

  const handleConfigureBack = () => {
    setConfigureOpen(false);
    if (!isConfigureInline) {
      setOpen(true);
    }
    if (isConfigureRoute(pathname)) {
      setNewChat(true);
      setIsIncognitoChat(false);
      if (userId) {
        clearUserSelection(userId);
      }
      navigate({ pathname: '/chat', search: '' });
    }
  };

  const handleSidebarToggle = () => {
    if (configureOpen) {
      setConfigureOpen(false);
      if (!isConfigureInline) {
        setOpen(true);
      }
      if (isConfigureRoute(pathname)) {
        setNewChat(true);
        setIsIncognitoChat(false);
        if (userId) {
          clearUserSelection(userId);
        }
        navigate({ pathname: '/chat', search: '' });
      }
      return;
    }
    toggleSidebar();
  };

  const isChatLikeRoute =
    pathname === '/chat' ||
    pathname.startsWith('/chat/') ||
    pathname === '/agentChat' ||
    pathname.startsWith('/agentChat/');

  const prevPathnameRef = useRef(pathname);

  useEffect(() => {
    const previousPathname = prevPathnameRef.current;
    const pathChanged = previousPathname !== pathname;
    prevPathnameRef.current = pathname;

    const onConfigureRoute = isConfigureRoute(pathname);
    if (onConfigureRoute) {
      setConfigureOpen(true);
      if (!isConfigureInline) {
        setOpen(false);
      } else if (isSidebarOverlay) {
        if (shouldOpenConfigureOverlayOnPathChange(previousPathname, pathname)) {
          setOpenMobile(true);
        }
      } else {
        setOpen(true);
      }
    } else {
      setConfigureOpen(false);
      // Auto-expand when navigating onto a chat route (e.g. Start Chat from Configure/Agents).
      if (pathChanged && isChatLikeRoute && !isSidebarOverlay) {
        setOpen(true);
      }
    }
  }, [pathname, isSidebarOverlay, isConfigureInline, isChatLikeRoute]);

  const sidebarExpanded = isSidebarOverlay || open;
  const showExpandedNav = sidebarExpanded;
  const showChatSections =
    sidebarExpanded &&
    !configureOpen &&
    hasValidConversationComponentRoute(pathname) &&
    effectivePlanName !== PLANS.NOPLAN;

  const onIncognitoClick = () => {
    if (isSidebarOverlay) {
      setOpenMobile(false);
    }
    setNewChat(true);
    setIsIncognitoChat(true);
    navigate('/chat?incognito-chat=true');
  };

  // const scrollToGroupChats = () => {
  //   if (isMobile) {
  //     setOpenMobile(false);
  //   }
  //   document.getElementById('sidebar-group-chats')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  // };

  useEffect(() => {
    if (!isSidebarOverlay) {
      setOpenMobile(false);
    }
  }, [pathname, setOpenMobile, isSidebarOverlay]);

  const sidebarStyle = !isSidebarOverlay
    ? ({
      width:
        configureOpen && !isConfigureInline
          ? 'var(--sidebar-icon-rail-width)'
          : open
            ? 'var(--sidebar-width)'
            : '72px',
      minWidth:
        configureOpen && !isConfigureInline
          ? 'var(--sidebar-icon-rail-width)'
          : open
            ? 'var(--sidebar-width)'
            : '72px',
      transition: 'width 0.3s ease, min-width 0.3s ease',
      height: '100%',
      borderRight: configureOpen ? '0px solid transparent' : '0px solid var(--border-color, #e2e8f0)',
    } as React.CSSProperties)
    : undefined;

  const sidebarContent = (
    <Sidebar
      className={cn(
        'bg-sidebar-surface dark:bg-sidebar h-full border-[#E7E7E7] dark:border-sidebar-border',
        configureOpen && !isConfigureInline ? 'border-r-0' : 'border-r'
      )}
      collapsible="icon"
      style={sidebarStyle}
    >
      <div
        className={cn(
          'flex h-full min-h-0 flex-col gap-6 bg-sidebar-surface px-2 py-6 dark:bg-transparent'
        )}
      >
        <SidebarHeader className="!p-0 shrink-0">
          {type === SidebarHeaderVariant.Logo && (
            <LogoSidebarHeader
              type={type}
              logo={logo}
              smallLogo={smallLogo}
              open={open}
              isMobile={isSidebarOverlay}
              setOpenMobile={setOpenMobile}
            />
          )}

          {type === SidebarHeaderVariant.Icons && (
            <IconsSidebarHeader
              type={type}
              strokeWidth={strokeWidth}
              toggleSidebar={handleSidebarToggle}
              onIncognitoClick={onIncognitoClick}
              onNewChatClick={onNewChatClick}
              currentPlan={currentPlan}
              disableNewChat={disableNewChat}
              open={open}
              isMobile={isSidebarOverlay}
            />
          )}
        </SidebarHeader>

        <SidebarContent className="text-base mr-0 min-h-0 flex-1 text-high-emphasis font-normal group-data-[collapsible=icon]:ml-0 group-data-[collapsible=icon]:pr-0 !overflow-hidden flex flex-col">
          <div className="flex-shrink-0 bg-transparent">
            <SidebarPrimaryNavItem
              label={t('NEW_CHAT')}
              icon="new-chat"
              to={{ pathname: '/chat', search: '' }}
              onClick={onNewChatClick}
              isActive={isPathActive(pathname, '/chat')}
              disabled={disableNewChat}
              showText={showExpandedNav}
            />
            <SidebarPrimaryNavItem
              label={t('AGENTS')}
              icon="agents"
              to="/agents"
              isActive={isPathActive(pathname, '/agents') || pathname.startsWith('/agent-details')}
              showText={showExpandedNav}
              onClick={() => {
                setNewChat(false);
                if (isSidebarOverlay) setOpenMobile(false);
              }}
            />
            {effectivePlanName !== PLANS.NOPLAN && (
              <SidebarPrimaryNavItem
                label={t('MEDIA_LIBRARY', { defaultValue: 'Media Library' })}
                icon="library"
                to="/media-library"
                isActive={isPathActive(pathname, '/media-library')}
                showText={showExpandedNav}
                onClick={() => {
                  setNewChat(false);
                  if (isSidebarOverlay) setOpenMobile(false);
                }}
              />
            )}
            <SidebarPrimaryNavItem
              label={t('SCHEDULED') || 'Scheduled'}
              icon="scheduled"
              to="/scheduler"
              isActive={isPathActive(pathname, '/scheduler')}
              showText={showExpandedNav}
              onClick={() => {
                setNewChat(false);
                if (isSidebarOverlay) setOpenMobile(false);
              }}
            />
            <SidebarPrimaryNavItem
              label={t('TOOLS')}
              icon="tools"
              to="/tools"
              isActive={isPathActive(pathname, '/tools') || pathname.startsWith('/tool-details')}
              showText={showExpandedNav}
              onClick={() => {
                setNewChat(false);
                if (isSidebarOverlay) setOpenMobile(false);
              }}
            />
            <SidebarConfigureNavItem
              showText={showExpandedNav}
              showAsBack={isConfigureInline && configureOpen}
              isActive={configureOpen || isConfigureRoute(pathname)}
              onClick={isConfigureInline && configureOpen ? handleConfigureBack : handleConfigureClick}
            />
          </div>

          {configureOpen && isConfigureInline && (
            <div className="min-h-0 flex-1 overflow-x-hidden overflow-y-auto">
              <SidebarConfigurePanel onBack={handleConfigureBack} />
            </div>
          )}

          {showChatSections ? (
            <DndContext
              sensors={sensors}
              onDragStart={handleDragStart}
              onDragEnd={handleDragEnd}
              onDragCancel={handleDragCancel}
              onDragOver={handleDragOver}
              modifiers={[restrictToVerticalAxis]}
            >
              <div
                ref={sidebarScrollRef}
                onScroll={loadMoreIfNearBottom}
                className="min-h-0 flex-1 overflow-x-hidden overflow-y-auto bg-transparent [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden pr-0.5"
              >
                <div className="flex flex-col gap-3 px-4 py-4">
                  <AgentsSection
                    agents={agentsData?.agents}
                    chatId={activeSidebarChatId}
                  />
                  <ProjectsSection />

                  {isGroupChatEnabled && groupChats?.length > 0 && (
                    <div id="sidebar-group-chats">
                      <GroupChatsSection
                        conversations={groupChats}
                        isSidebarLoading={isSidebarGroupLoading}
                        renderRightAction={(id, title) => (
                          <ConversationContextMenu
                            options={createConversationMenuOptions(id, title, shareTokens, true)}
                          />
                        )}
                      />
                    </div>
                  )}

                  <IndividualChatsSection
                    conversations={individualChats}
                    isSidebarLoading={isSidebarIndividualLoading}
                    renderRightAction={(id, title) => (
                      <ConversationContextMenu
                        options={createConversationMenuOptions(id, title, shareTokens, false)}
                      />
                    )}
                  />
                </div>

                <CustomDialog
                  isOpen={isConfirmDialogOpen}
                  isLoading={isLoading}
                  setIsOpen={setIsConfirmDialogOpen}
                  title={
                    conversationState.type === 'delete'
                      ? t('DELETE')
                      : conversationState.type === 'leave'
                        ? t('LEAVE') || 'Leave'
                        : t('CONFIRM_ACTION')
                  }
                  onConfirm={handleConfirmAction}
                  confirmText={t('YES')}
                  cancelText={t('CANCEL')}
                  description={''}
                >
                  <p>
                    {conversationState.type === 'delete'
                      ? t('DELETE_CONFIRMATION_DESCRIPTION') || t('CONFIRM_ACTION_DESCRIPTION')
                      : conversationState.type === 'leave'
                        ? t('LEAVE_CONFIRMATION_DESCRIPTION') || 'Are you sure you want to leave this conversation?'
                        : t('CONFIRM_ACTION_DESCRIPTION')}
                  </p>
                </CustomDialog>

                <CustomDialog
                  isOpen={isRenameDialogOpen}
                  isLoading={isRenameLoading}
                  setIsOpen={setIsRenameDialogOpen}
                  title={t('RENAME')}
                  onConfirm={handleConfirmAction}
                  confirmText={t('SAVE')}
                  cancelText={t('CANCEL')}
                  description={''}
                  isConfirmDisabled={newTitle.trim() === ''}
                >
                  <DebouncedInput
                    placeholder={t('ENTER_NEW_TITLE')}
                    initialValue={newTitle}
                    onDebouncedChange={setNewTitle}
                    className="
                            h-10 w-full rounded-3xl
                            border border-primary
                            bg-background pr-8
                            focus:outline-none
                            focus:border-primary
                            focus:ring-1 focus:ring-primary
                 "
                  />
                </CustomDialog>

                <NewProjectModal
                  callback={() => {
                    refetchProject(queryClient, '');
                    removeConversationFromSidebarById(conversationIdToAddToProject!);
                  }}
                  newConversatioId={conversationIdToAddToProject}
                  isOpen={isAddProjectModalOpen}
                  onClose={() => setIsAddProjectModalOpen(false)}
                />

                {/* DragOverlay for visual feedback */}
                <DragOverlay modifiers={[restrictToVerticalAxis]}>
                  {isDragging && activeConversation ? (
                    <div className="bg-primary-25 rounded-lg px-3 py-2 shadow-lg border border-primary z-[9999]">
                      <p className={`text-sm truncate max-w-[200px] ${theme.theme === 'dark' ? 'text-white' : 'text-foreground'}`}>
                        📄 {activeConversation.title}
                      </p>
                    </div>
                  ) : null}
                </DragOverlay>
              </div>
            </DndContext>
          ) : sidebarExpanded && !configureOpen ? (
            <div className="min-h-0 flex-1 overflow-x-hidden overflow-y-auto bg-transparent [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden pr-0.5">
              <div className="flex flex-col gap-3 px-4 py-4">
                <AgentsSection
                  agents={agentsData?.agents}
                  chatId={activeSidebarChatId}
                />
                <ProjectsSection />
              </div>
            </div>
          ) : null}
        </SidebarContent>

        <AppSidebarFooter />
      </div>

      {isGroupChatEnabled && (
        <GroupShareModal
          showModal={showModal}
          setShowModal={setShowModal}
          shareToken={shareToken}
          setShareToken={setShareToken}
          isLoadingConversation={isLoadingModal}
        />
      )}
    </Sidebar>
  );

  return (
    <>
      {sidebarContent}
      <ProjectActionDialogs />
      {configureOpen && !isConfigureInline && (
        <div className="fixed inset-y-0 left-[var(--sidebar-icon-rail-width)] z-[35] hidden md:block">
          <SidebarConfigurePanel onBack={handleConfigureBack} />
        </div>
      )}
    </>
  );
}
